import { t } from '../i18n/helpers';
import { calculateAdjustedPrice, calculateOrderTotal } from './pricingCalculator';

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

export const itemBaseUnit = (item) => Number(item.baseUnitPrice ?? item.unitPrice ?? 0);
export const itemFinalUnit = (item) => Number(item.finalUnitPrice ?? item.unitPrice ?? itemBaseUnit(item));
export const itemLineTotal = (item) => round2(itemFinalUnit(item) * Number(item.quantity || 0));

export const getAppointmentPricing = (appointment) => {
  const base = Number(appointment?.pricing?.basePrice ?? appointment?.servicePrice ?? 0);
  const staffSurcharge = Number(appointment?.pricing?.staffSurchargeAmount || 0);
  const subtotal = round2(base + staffSurcharge);
  const final = Number(appointment?.pricing?.finalPrice ?? subtotal);
  const adjustmentDelta = round2(final - subtotal);
  const hasStaffSurcharge = staffSurcharge > 0;
  const hasAdjustment =
    (appointment?.pricing?.adjustmentType && appointment.pricing.adjustmentType !== 'NONE')
    || hasStaffSurcharge;
  return {
    base,
    staffSurcharge,
    subtotal,
    final,
    delta: adjustmentDelta,
    hasAdjustment,
    hasStaffSurcharge,
    staffSurchargeStaffName: appointment?.pricing?.staffSurchargeStaffName || '',
  };
};

export const adjustmentLabel = (type, value, mode) => {
  if (!type || type === 'NONE') return null;
  if (type === 'MANUAL_OVERRIDE') return t('pricing.customPrice');
  const suffix = mode === 'PERCENTAGE' ? `${value}%` : formatLkr(value);
  if (type === 'DISCOUNT') return t('pricing.discountWithValue', { value: suffix });
  if (type === 'SURCHARGE') return t('pricing.surchargeWithValue', { value: suffix });
  return type.replace(/_/g, ' ').toLowerCase();
};

export const orderLevelAdjustmentAmount = (subtotal, orderAdjustment) => {
  const adj = orderAdjustment || {};
  if (!adj.adjustmentType || adj.adjustmentType === 'NONE') return 0;
  const after = calculateAdjustedPrice(subtotal, adj.adjustmentType, adj.adjustmentValue, adj.adjustmentMode);
  return round2(after - subtotal);
};

export const buildOrderBreakdown = (order) => {
  const items = (order?.items || []).map((item) => {
    const base = itemBaseUnit(item);
    const final = itemFinalUnit(item);
    const lineTotal = itemLineTotal(item);
    const baseLine = round2(base * Number(item.quantity || 0));
    return { item, base, final, lineTotal, baseLine, itemDelta: round2(lineTotal - baseLine) };
  });

  const itemAdjustmentsTotal = round2(items.reduce((s, r) => s + r.itemDelta, 0));
  const subtotalFromItems = round2(items.reduce((s, r) => s + r.lineTotal, 0));
  const subtotalAmount = Number(order?.subtotalAmount ?? subtotalFromItems);
  const orderAdjAmount = orderLevelAdjustmentAmount(subtotalAmount, order?.orderAdjustment);
  const totalAmount = Number(order?.totalAmount ?? calculateOrderTotal(
    items.map((r) => ({ ...r.item, finalUnitPrice: r.final })),
    order?.orderAdjustment || {}
  ).totalAmount);

  return { items, itemAdjustmentsTotal, subtotalAmount, orderAdjAmount, totalAmount };
};
